import { memo } from "react";

import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { BillingLoadState } from "stores/BillingStore";

import { GenericObject } from "utility/misc";

import useAnalytics from "effect/use_analytics";
import { useBilling } from "effect/use_billing";

import LoaderModal from "layouts/components/LoaderModal";

import CancelPlan from "./CancelPlan";
import CurrentFeatures from "./CurrentFeatures";
import TierFeatures from "./NextTierFeatures";

// -----------------------------------------------------------------------------

const Trial = {
  label: "Trial",
  upgrade_to: "Launch",
  features: [
    "Fully featured 14-day free trial",
    "Unlimited supported sources and destinations",
    "Single Sign-On (SSO) via Google or MagicLink",
  ],
};

const Launch = {
  label: "Launch Plan",
  upgrade_to: "Scale Plan",
  features: [
    ...Trial.features,
    "SAML 2.0 SSO",
    "Roles-based Access Control (RBAC)",
  ],
};

const Scale = {
  label: "Scale Plan",
  downgrade_to: "Launch",
  features: [
    ...Launch.features,
    "Automated Fixes",
    "Dedicated Account Manager",
    "Out of the box Compliance Framework Support (SOC2, CIS, etc.)",
  ],
};

const Tier: GenericObject = {
  Trial,
  Launch,
  Scale,
};

// -----------------------------------------------------------------------------

const BillingManagePlanManagePlan = memo(
  function BillingManagePlanManagePlan(): JSX.Element {
    useAnalytics("Billing Dashboard: Manage Subscription - General");

    //
    const [billingStore] = useBilling();

    //
    if (billingStore.state === BillingLoadState.Error) {
      return (
        <Alert color="error">
          There was an error loading your subscription data
        </Alert>
      );
    }

    //
    const {
      state = BillingLoadState.Loading,
      subscription: {
        label: planName = "Unknown",
        is_trial: isTrial = true,
        price = 0,
        days_left: daysLeft = 0,
        interval = "month",
      } = {},
    } = billingStore;
    const tier = Tier[planName] ?? Tier.Trial;
    const {
      upgrade_to: upgradeTo,
      downgrade_to: downgradeTo,
      features: tierFeatures = [],
    } = tier;

    const nextTier = Tier[upgradeTo || downgradeTo];
    const isDowngrade = downgradeTo !== undefined;
    const nextTierDirection = isDowngrade ? "Downgrade" : "Upgrade";
    const nextTierFeatures = isDowngrade
      ? (nextTier?.features ?? []).filter((feature: string) =>
          tierFeatures.includes(feature)
        )
      : (nextTier?.features ?? []).filter(
          (feature: string) => !tierFeatures.includes(feature)
        );

    //
    return state === BillingLoadState.Loading ? (
      <LoaderModal />
    ) : (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={8} lg={8} sx={GridLeftColumnSX}>
          <Typography gutterBottom variant="h6" fontWeight="bold">
            My Subscription
          </Typography>
          <CurrentFeatures
            label={planName}
            features={tierFeatures}
            daysLeft={daysLeft}
            price={price}
            interval={interval}
            isTrial={isTrial}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={4}>
          <Typography gutterBottom variant="h6" fontWeight="bold">
            Manage Subscription
          </Typography>
          <Stack spacing={2}>
            {nextTier ? (
              <TierFeatures
                label={planName}
                nextLabel={nextTier.label}
                direction={nextTierDirection}
                isDowngrade={isDowngrade}
                features={nextTierFeatures}
              />
            ) : (
              (null as any)
            )}
            <CancelPlan />
          </Stack>
        </Grid>
      </Grid>
    );
  }
);

export default BillingManagePlanManagePlan;

// -----------------------------------------------------------------------------

const GridLeftColumnSX = { display: "flex", flexDirection: "column" };
